import axios, { isAxiosError } from 'axios';

import { assertClientSide, webpack } from '@dr.pogodin/react-utils';

import type {
  ChangeAdminStatusBodyT,
  ChangeAdminStatusResT,
  CreateAdminUserBodyT,
  CreateAdminUserResT,
  GetAdminUsersResT,
  GetUserArtistResT,
  UpdateUserBodyT,
  UpdateUserResT,
  UserUpdateT,
} from 'server/controllers/user';

import type { UpdateAvatarResT } from 'server/controllers/user/updateAvatar';

import type * as SSNS from 'server/services/user';

import {
  type AdminStatusT,
  type ArtistT,
  E_ARTIST,
  E_AVATAR,
  P_API_ADMIN_USERS,
  P_API_USERS,
  type UserT,
  getConfig,
} from 'utils';

export * from './get';
export * from './getAiProfile';
export * from './list';

const ss = webpack.requireWeak<typeof SSNS>('server/services/user', import.meta.dirname);

export async function getAdminUsers(): Promise<UserT[]> {
  if (ss) {
    const list = await ss.listUsers({
      kind: 'ADMIN',
      pageSize: Number.MAX_SAFE_INTEGER,
    });
    return list.users;
  }

  const res = await axios<GetAdminUsersResT>(P_API_ADMIN_USERS);

  return res.data;
}

/**
 * Gets artist record for the specified user ID, or `null` if the user has
 * no associated artist record (e.g. a plain OWNER account).
 */
export async function getUserArtist(userId: string): Promise<ArtistT | null> {
  if (ss) return ss.getUserArtist(userId);

  try {
    const res = await axios<GetUserArtistResT>(
      `${P_API_USERS}/${userId}${E_ARTIST}`,
    );
    return res.data;
  } catch (error) {
    if (isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function setAvatar(
  userId: string,
  avatar: File,
): Promise<void> {
  assertClientSide();

  const config = await getConfig();

  await axios.put<UpdateAvatarResT>(
    `${P_API_USERS}/${userId}${E_AVATAR}`,
    avatar,
    {
      headers: {
        'Content-Type': avatar.type,
        'csrf-token': config.CSRF,
      },
    },
  );
}

type CreateAdminUserT = {
  email: string;
  firstName: string;
  lastName: string;
};

export async function createAdminUser({
  email,
  firstName,
  lastName,
}: CreateAdminUserT): Promise<CreateAdminUserResT> {
  if (ss) throw Error('Client-side only');

  const config = await getConfig();

  const res = await axios.post<CreateAdminUserResT>(P_API_ADMIN_USERS, {
    _csrf: config.CSRF,
    email,
    firstName,
    lastName,
  } satisfies CreateAdminUserBodyT);

  return res.data;
}

export async function changeAdminStatus(
  userId: string,
  status: AdminStatusT,
): Promise<ChangeAdminStatusResT> {
  const config = await getConfig();

  const res = await axios.post<ChangeAdminStatusResT>(
    `${P_API_ADMIN_USERS}/${userId}`,
    {
      _csrf: config.CSRF,
      status,
    } satisfies ChangeAdminStatusBodyT,
  );

  return res.data;
}

export async function removeAdminUser(userId: string): Promise<void> {
  const config = await getConfig();

  await axios.delete(`${P_API_ADMIN_USERS}/${userId}`, {
    headers: { 'csrf-token': config.CSRF },
  });
}

export async function updateUser(
  id: string,
  update: UserUpdateT,
): Promise<UserT> {
  assertClientSide();

  const config = await getConfig();

  const res = await axios.patch<UpdateUserResT>(`${P_API_USERS}/${id}`, {
    _csrf: config.CSRF,
    ...update,
  } satisfies UpdateUserBodyT);

  return res.data;
}
